/**
 * Compares two arrays and determines whether they are equal.
 *
 * @param firstArray - The first array to compare.
 * @param secondArray - The second array to compare.
 * @param checkOrder - Whether to consider the order of elements in the arrays when comparing.
 * @returns A boolean value indicating whether the two arrays are equal.
 */
const compareArrays = (
  firstArray: any[],
  secondArray: any[],
  checkOrder: boolean,
) => {
  if (checkOrder) {
    const valueFirstObj = JSON.stringify(firstArray);
    const valueSecondObj = JSON.stringify(secondArray);
    if (valueFirstObj !== valueSecondObj) {
      return true;
    }
  }
  const filteredArray = firstArray.filter((item) => {
    const valueNotChanged = secondArray?.some(
      (data: any) => JSON.stringify(data) === JSON.stringify(item),
    );
    return valueNotChanged;
  });
  if (firstArray.length !== filteredArray.length) {
    return true;
  }
  if (firstArray.length !== secondArray.length) {
    return true;
  }
  return false;
};

/**
 * Compares two objects and determines whether they are equal.
 *
 * @param firstObj - The first object to compare.
 * @param secondObj - The second object to compare.
 * @param checkOrder - Whether to consider the order of elements in arrays when comparing.
 * @returns A boolean value indicating whether the two objects are equal.
 */
const deepComparison: any = (
  firstObj: any,
  secondObj: any,
  checkOrder: boolean,
) => {
  if (!firstObj || !secondObj) return true;
  if (Array.isArray(firstObj)) {
    return compareArrays(firstObj, secondObj, checkOrder);
  }
  return Object.keys(firstObj).some((property) => {
    if (typeof firstObj[property] === 'object' && firstObj[property] !== null) {
      return deepComparison(
        firstObj[property],
        secondObj[property],
        checkOrder,
      );
    } else {
      if (firstObj && !secondObj) return true;
      const valueFirstObj = JSON.stringify(firstObj[property]);
      const valueSecondObj = JSON.stringify(secondObj[property]);
      if (valueFirstObj !== valueSecondObj) {
        return true;
      }
    }
    return false;
  });
};

/**
 * Compares two objects and determines whether they have changed.
 *
 * @param firstObj - The first object to compare.
 * @param secondObj - The second object to compare.
 * @param checkOrder - Whether to consider the order of elements in arrays when comparing.
 * @returns A boolean value indicating whether the two objects have changed.
 */
const compareObjectsChanges = (
  firstObj: any,
  secondObj: any,
  checkOrder = false,
) => {
  const isChanged = deepComparison(firstObj, secondObj, checkOrder);
  return isChanged;
};

export default compareObjectsChanges;
